import React, { useEffect, useRef } from "react";
import { ActivityFormStyled } from "./styled";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { MdCancel, MdCheckCircle, MdDelete } from "react-icons/md";
import AnswerForm from "./AnswerForm";
import moment from "moment";
import { toast } from "react-toastify";
import { Buttons, Cards, Displays, Inputs } from "components";

const ActivityForm = ({
  id,
  initialValues,
  businessUnitOptions,
  permission,
  onCancel,
  onDelete,
  onDeleteQuestion,
  onSent,
  onSubmit,
  onDuplicate,
  seatStatus,
  onGoSeatSetting,
}) => {
  const schema = yup.object().shape({
    type: yup.string().required("Activity type is required"),
    // condition_type: yup.string().required("Condition type is required"),
    // gift_type: yup.string().required("Gift type is required"),
    code: yup.string().required("Activity Code is required"),
    short_title_en: yup.string().when("type", {
      is: (value) => value !== "4",
      then: (schema) => schema.required("Activity name is required"),
    }),
    short_title_th: yup.string().when("type", {
      is: (value) => value !== "4",
      then: (schema) => schema.required("Activity name is required"),
    }),
    short_description_en: yup.string().when("type", {
      is: (value) => value !== "4",
      then: (schema) => schema.required("Activity name is required"),
    }),
    short_description_th: yup.string().when("type", {
      is: (value) => value !== "4",
      then: (schema) => schema.required("Activity name is required"),
    }),
    name_en: yup.string().required("Activity name is required"),
    name_th: yup.string().required("Activity name is required"),
    description_en: yup.string().required("Description is required"),
    description_th: yup.string().required("Description is required"),
    // point: yup.string().required("Point name is required"),
    // permission_type: yup.string().required("Required"),
    // last_date: yup.string().required("Required"),
    // announcement_date: yup.string().required("Required"),
    // qrcode_amount: yup.string().required('Qrcode amount is required'),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "question_list",
  });

  const watchType = watch("type");
  const watchConditionType = watch("condition_type");
  const watchUseQRcodeSpecial = watch("is_used_qrcode_special");
  const watchPermissionType = watch("permission_type");
  const watchIsCondition = watch("is_condition");
  const watchAnnouncementType = watch("announcement_type");
  const watchQuestionList = watch("question_list");
  const watchIsSeatBooking = watch("is_seat_booking");

  // Booking Seat ใช้ได้เฉพาะ Offline: flow จองไม่ผ่าน register เดิม จึงซ่อนและล็อกค่าแต้ม/เงื่อนไข/สิทธิ์ (API บังคับซ้ำอีกชั้น)
  // ล็อกค่าไว้ใน form ด้วย validation เดิมใน _onSubmit จึงผ่านโดยไม่ต้องแก้
  const isSeatBooking = watchType === "2" && !!watchIsSeatBooking;
  // เก็บค่าก่อนเปิด Booking Seat ไว้คืนตอนปิดสวิตช์ ไม่งั้นแค่กดเปิด-ปิดค่าเดิมของกิจกรรมจะถูกเขียนทับ
  const _beforeSeatBookingRef = useRef(null);
  useEffect(() => {
    const SEAT_LOCKED_FIELDS = ["condition_type", "gift_type", "point", "permission_type"];
    if (isSeatBooking) {
      if (!_beforeSeatBookingRef.current) {
        _beforeSeatBookingRef.current = getValues(SEAT_LOCKED_FIELDS);
      }
      setValue("condition_type", "1");
      setValue("gift_type", "1");
      setValue("point", 0);
      setValue("permission_type", "1");
    } else if (_beforeSeatBookingRef.current) {
      SEAT_LOCKED_FIELDS.forEach((key, i) => setValue(key, _beforeSeatBookingRef.current[i]));
      _beforeSeatBookingRef.current = null;
    }
  }, [isSeatBooking, setValue, getValues]);
  // เปลี่ยน type ออกจาก Offline (ตอนสร้าง) ต้องปิด flag ไม่งั้น API ปฏิเสธ
  useEffect(() => {
    if (watchType && watchType !== "2" && watchIsSeatBooking) {
      setValue("is_seat_booking", false);
      setValue("is_unlisted", false);
    }
  }, [watchType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (watchType === "4") {
      setValue("condition_type", "4");
      setValue("permission_type", "1");
    }
  }, [watchType, setValue]);

  const watchGiftType = watch("gift_type");
  // gift_type "4" (Certificate) ใช้ได้เฉพาะ offline + ลูกค้าสแกนเอง
  const isCertificateGiftAllowed =
    watchType === "2" && watchConditionType === "5";
  const isCertificateGift = watchGiftType === "4";
  // เปลี่ยน type/condition แล้ว "4" ใช้ไม่ได้อีก ต้องล้างทิ้ง
  // ไม่งั้นบันทึกไปเจอ API reject และถ้าหลุดไปคู่ condition "2"/"4" จะทำ register() ค้าง
  useEffect(() => {
    if (watchGiftType === "4" && !isCertificateGiftAllowed) {
      setValue("gift_type", "");
    }
  }, [watchType, watchConditionType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialValues) {
      let params = {
        ...initialValues,
        business_unit_ref_id: initialValues.business_unit_ref_id
          ? initialValues.business_unit_ref_id
          : "null",
        start_date:
          initialValues.start_date && new Date(initialValues.start_date),
        end_date: initialValues.end_date && new Date(initialValues.end_date),
        img_file: initialValues.img ? [{ media_uri: initialValues.img }] : [],
        img_file2: initialValues.img2
          ? [{ media_uri: initialValues.img2 }]
          : [],
      };
      if (initialValues.question_list) {
        params.question_list = initialValues.question_list.map((e) => ({
          ...e,
          image: e.img_path ? [{ media_uri: e.img_path }] : [],
        }));
      }
      if (initialValues.registration_start_time) {
        params.registration_start_time = new Date(
          moment(initialValues.registration_start_time, "HH:mm"),
        );
      }
      if (initialValues.registration_end_time) {
        params.registration_end_time = new Date(
          moment(initialValues.registration_end_time, "HH:mm"),
        );
      }
      reset({
        ...params,
      });
    }
  }, [initialValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const _onSubmit = (values) => {
    let isError = false;
    if (values.type !== "3") {
      if (!values.permission_type) {
        isError = true;
        setError("permission_type", {
          type: "custom",
          message: "Permission type is required",
        });
      }
      // gift_type "4" ไม่มีแต้ม ช่อง Point ถูกซ่อน จึงต้องไม่ validate
      // ไม่งั้น setError ยิงใส่ฟิลด์ที่มองไม่เห็น กด Save ไม่ผ่านโดยไม่มี error โผล่ที่ไหน
      if (
        values.gift_type !== "4" &&
        !values.point &&
        values.point !== 0
      ) {
        isError = true;
        setError("point", {
          type: "custom",
          message: "Point is required",
        });
      }
      if (!values.condition_type) {
        isError = true;
        setError("condition_type", {
          type: "custom",
          message: "Condition type is required",
        });
      }
      if (!values.gift_type) {
        isError = true;
        setError("gift_type", {
          type: "custom",
          message: "Gift type is required",
        });
      }
    }
    // condition_type "5" เฟสนี้ล็อกเป็นสแกนครั้งเดียว ซ่อน 2 ฟิลด์นี้และให้ API บังคับเป็น 1
    // จึงต้องไม่ validate ด้วย ไม่งั้น setError ยิงใส่ฟิลด์ที่มองไม่เห็น กด Save ไม่ผ่านแบบไร้ร่องรอย
    if (values.type === "1" && watchConditionType !== "2") {
      if (!values.qrcode_amount) {
        isError = true;
        setError("qrcode_amount", {
          type: "custom",
          message: "Qrcode amount is required",
        });
      }
      if (!values.qrcode_passed_amount) {
        isError = true;
        setError("qrcode_passed_amount", {
          type: "custom",
          message: "Qrcode passed amount is required",
        });
      } else if (
        values.qrcode_amount &&
        Number(values.qrcode_passed_amount) > Number(values.qrcode_amount)
      ) {
        // กันตั้งค่าจนลูกค้าทำเงื่อนไขไม่สำเร็จ (ต้องสแกนมากกว่าจำนวน qrcode ที่มี)
        isError = true;
        setError("qrcode_passed_amount", {
          type: "custom",
          message: "ต้องไม่มากกว่า QRCode Amount",
        });
      }
      // condition_type "5" ไม่มี qrcode พิเศษ ฟิลด์ถูกซ่อน จึงต้องไม่ validate
      // ไม่งั้นถ้าข้อมูลเก่าเคยเปิดไว้ จะติด error ที่ฟิลด์ที่มองไม่เห็น กดบันทึกไม่ผ่าน
      if (
        watchConditionType !== "5" &&
        values.is_used_qrcode_special &&
        !values.qrcode_special_point
      ) {
        isError = true;
        setError("qrcode_special_point", {
          type: "custom",
          message: "Qrcode special point is required",
        });
      }
    }
    if (values.permission_type === "2") {
      if (!values.permission_main_quotar) {
        isError = true;
        setError("permission_main_quotar", {
          type: "custom",
          message: "Required",
        });
      }
      if (
        values.announcement_type === "2" &&
        !values.permission_reserve_quotar
      ) {
        isError = true;
        setError("permission_reserve_quotar", {
          type: "custom",
          message: "Required",
        });
      }
      if (!values.announcement_type) {
        isError = true;
        setError("announcement_type", {
          type: "custom",
          message: "Required",
        });
      }
      if (values.announcement_type === "2" && !values.announcement_date) {
        isError = true;
        setError("announcement_date", {
          type: "custom",
          message: "Required",
        });
      }
      if (!values.registration_end_date) {
        isError = true;
        setError("registration_end_date", {
          type: "custom",
          message: "Required",
        });
      }
      // if (!values.last_date) {
      //   isError = true;
      //   setError("last_date", {
      //     type: "custom",
      //     message: "Required",
      //   });
      // }
    }
    if (!isError) {
      onSubmit(values);
    } else {
      toast.error("กรุณากรอกเงื่อนไขให้ครบ");
    }
  };

  const _handleCopyUrl = async (type) => {
    try {
      switch (type) {
        case "4":
          await navigator.clipboard.writeText(
            `${process.env.REACT_APP_DOMAIN}?redirect=activity-qr-register&id=${initialValues._id}`,
          );
          break;
        default:
          await navigator.clipboard.writeText(
            `${process.env.REACT_APP_DOMAIN}?redirect=activity&id=${initialValues._id}`,
          );
          break;
      }

      toast.success("คัดลอกข้อความแล้ว!");
    } catch (err) {
      toast.error("เกิดข้อผิดพลาดในการคัดลอก");
    }
  };

  return (
    <ActivityFormStyled>
      <form onSubmit={handleSubmit(_onSubmit)}>
        {id === "create" ? (
          <div className="duplicate_wrap">
            <div className="duplicate_btn">
              <Buttons.BgStandard
                type="button"
                label="Duplicate Activity"
                onClick={onDuplicate}
              />
            </div>
          </div>
        ) : (
          <div className="duplicate_wrap">
            <div className="duplicate_btn">
              <Buttons.BgStandard
                type="button"
                label="Copy url"
                onClick={() => _handleCopyUrl(watchType)}
              />
            </div>
          </div>
        )}
        <div className="input_row">
          <div className="f_left_col">Activity Type</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.Dropdown
                  {...field}
                  options={[
                    {
                      value: "1",
                      label: "Online",
                    },
                    {
                      value: "2",
                      label: "Offline",
                    },
                    {
                      value: "3",
                      label: "ฟอร์มทั่วไป",
                    },
                    {
                      value: "4",
                      label: "QR with out Register",
                    },
                  ]}
                  errors={errors.type?.message}
                  disabled={id !== "create"}
                />
              )}
              name="type"
              // defaultValue={{ label: '1', value: '1' }}
            />
          </div>
        </div>
        {watchType === "2" && (
          <div className="input_row">
            <div className="f_left_col">Booking Seat</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => <Inputs.InputSwitch {...field} value={!!field.value} />}
                name="is_seat_booking"
                defaultValue={false}
              />
              {isSeatBooking && (
                <div>
                  ***กิจกรรมจองที่นั่ง: ลูกค้าเลือกที่นั่ง ชำระเงินแนบสลิป แอดมินอนุมัติ ไม่ใช้แต้ม/เงื่อนไข/จำกัดสิทธิ์
                  ตั้งราคา ผังที่นั่ง และตรวจสลิปได้ที่หน้า Booking Seat (ไอคอน Action ในตารางกิจกรรม)
                </div>
              )}
            </div>
          </div>
        )}
        <div className="input_row">
          <div className="f_left_col">Business Unit</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.Dropdown
                  {...field}
                  options={businessUnitOptions}
                  errors={errors.type?.message}
                />
              )}
              name="business_unit_ref_id"
              // defaultValue={{ label: '1', value: '1' }}
            />
          </div>
        </div>
        {watchType !== "3" && !isSeatBooking && (
          <div className="input_row">
            <div className="f_left_col">Condition Type</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.Dropdown
                    {...field}
                    disabled={watchType === "4"}
                    options={
                      watchType === "4"
                        ? [
                            {
                              value: "4",
                              label: "แสกนแล้วได้รับรางวัลทันที",
                            },
                          ]
                        : watchType === "2"
                          ? [
                              {
                                value: "1",
                                label:
                                  "ทำตามเงื่อนไข scan qrcode (แอดมินสแกน 1 ครั้ง)",
                              },
                              {
                                value: "2",
                                label: "ลงทะเบียนได้รับรางวัลทันที",
                              },
                              {
                                value: "5",
                                label:
                                  "สแกน qrcode ระหว่างงาน (ลูกค้าสแกนเอง รับทันที)",
                              },
                            ]
                          : [
                              {
                                value: "1",
                                label: "ทำตามเงื่อนไข scan qrcode",
                              },
                              {
                                value: "2",
                                label: "ลงทะเบียนได้รับรางวัลทันที",
                              },
                            ]
                    }
                    errors={errors.condition_type?.message}
                  />
                )}
                name="condition_type"
                // defaultValue={{ label: '1', value: '1' }}
              />
            </div>
          </div>
        )}
        {watchType !== "3" && !isSeatBooking && (
          <div className="input_row">
            <div className="f_left_col">Gift Type</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.Dropdown
                    {...field}
                    options={[
                      {
                        value: "1",
                        label: "คะแนน",
                      },
                      {
                        value: "2",
                        label: "เหรียญเล่นเกมส์",
                      },
                      ...(isCertificateGiftAllowed
                        ? [{ value: "4", label: "Certificate" }]
                        : []),
                    ]}
                    errors={errors.gift_type?.message}
                  />
                )}
                name="gift_type"
                // defaultValue={{ label: '1', value: '1' }}
              />
              {isCertificateGift && (
                <div>
                  ***ลูกค้าจะไม่ได้คะแนน แต่จะได้ใบ Certificate ทางอีเมล
                  ต้องไปผูกไฟล์ใบที่เมนู Certificate → New Certificate →
                  เลือก Activity ให้เสร็จก่อนเปิดกิจกรรม
                </div>
              )}
            </div>
          </div>
        )}
        <div className="input_row">
          <div className="f_left_col">Code</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.TextField {...field} errors={errors.code?.message} />
              )}
              name="code"
              defaultValue=""
            />
          </div>
        </div>

        <div className="input_row lang_row">
          <div className="f_left_col">Short Title</div>
          <div className="f_right_col">
            <Inputs.InputBilingual
              control={control}
              errors={errors}
              nameTh="short_title_th"
              nameEn="short_title_en"
            />
          </div>
        </div>
        {watchType !== "4" && (
          <div className="input_row lang_row">
            <div className="f_left_col">Short Description</div>
            <div className="f_right_col">
              <Inputs.InputBilingual
                control={control}
                errors={errors}
                nameTh="short_description_th"
                nameEn="short_description_en"
              />
            </div>
          </div>
        )}
        <div className="input_row lang_row">
          <div className="f_left_col">Name</div>
          <div className="f_right_col">
            <Inputs.InputBilingual
              control={control}
              errors={errors}
              nameTh="name_th"
              nameEn="name_en"
            />
          </div>
        </div>
        <div className="input_row lang_row">
          <div className="f_left_col">Description</div>
          <div className="f_right_col">
            <Inputs.InputBilingual
              control={control}
              errors={errors}
              nameTh="description_th"
              nameEn="description_en"
              isWysiwyg
            />
          </div>
        </div>
        <div className="input_row">
          <div className="f_left_col">Start Date</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.DateTime
                  {...field}
                  placeholder={"Start Date"}
                  errors={errors.start_date?.message}
                  showYearDropdown
                />
              )}
              name="start_date"
              defaultValue={new Date()}
            />
          </div>
        </div>
        <div className="input_row">
          <div className="f_left_col">End Date</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.DateTime
                  {...field}
                  placeholder={"End Date"}
                  errors={errors.end_date?.message}
                  showYearDropdown
                />
              )}
              name="end_date"
              defaultValue={new Date()}
            />
          </div>
        </div>
        <div className="input_row">
          <div className="f_left_col">
            {watchType === "4"
              ? "Picture show on registered success page"
              : "Picture"}
          </div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.UploadImage
                  {...field}
                  theme_upload_image
                  isDelete
                  fieldName="img"
                  acceptType={"cover"}
                  scaleLabel="*** รูปขนาดกว้าง 720px สูง 720px"
                  errors={errors.img_file?.message}
                />
              )}
              name="img_file"
              defaultValue={[]}
            />
          </div>
        </div>
        {watchType !== "4" && (
          <div className="input_row">
            <div className="f_left_col">Picture2</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.UploadImage
                    {...field}
                    isDelete
                    fieldName="img2"
                    theme_upload_image
                    acceptType={"cover"}
                    scaleLabel="*** รูปขนาดกว้าง 720px สูง 720px"
                    errors={errors.img_file?.message}
                  />
                )}
                name="img_file2"
                defaultValue={[]}
              />
            </div>
          </div>
        )}

        {watchType !== "3" && !isCertificateGift && !isSeatBooking && (
          <div className="input_row">
            <div className="f_left_col">Point</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextField
                    {...field}
                    type="number"
                    // placeholder={t('test')}
                    errors={errors.point?.message}
                  />
                )}
                name="point"
                defaultValue=""
              />
            </div>
          </div>
        )}
        {watchType === "1" && watchConditionType !== "2" && (
          <>
            <div className="input_row">
              <div className="f_left_col">QRCode Amount</div>
              <div className="f_right_col">
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.TextField
                      {...field}
                      type="number"
                      min={
                        !initialValues
                          ? 0
                          : initialValues.qrcode_list?.length || 0
                      }
                      // placeholder={t('test')}
                      errors={errors.qrcode_amount?.message}
                    />
                  )}
                  name="qrcode_amount"
                  defaultValue=""
                />
              </div>
            </div>
            <div className="input_row">
              <div className="f_left_col">QRCode Passed</div>
              <div className="f_right_col">
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.TextField
                      {...field}
                      type="number"
                      // placeholder={t('test')}
                      errors={errors.qrcode_passed_amount?.message}
                    />
                  )}
                  name="qrcode_passed_amount"
                  defaultValue=""
                />
              </div>
            </div>
            {/* condition_type "5" ไม่มี qrcode พิเศษ
                ลูกค้าสแกน qrcode ที่ติดตามจุดต่าง ๆ ให้ครบตามจำนวนอย่างเดียว */}
            {watchConditionType !== "5" && (
              <div className="input_row">
                <div className="f_left_col">Use special point</div>
                <div className="f_right_col">
                  <Controller
                    control={control}
                    render={({ field }) => (
                      <Inputs.InputSwitch
                        {...field}
                        errors={errors.is_used_qrcode_special?.message}
                      />
                    )}
                    name="is_used_qrcode_special"
                    defaultValue={false}
                  />
                </div>
              </div>
            )}
          </>
        )}
        {watchUseQRcodeSpecial && watchConditionType !== "5" && (
          <>
            <div className="input_row">
              <div className="f_left_col">QRCode special point</div>
              <div className="f_right_col">
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.TextField
                      {...field}
                      type="number"
                      // placeholder={t('test')}
                      errors={errors.qrcode_special_point?.message}
                    />
                  )}
                  name="qrcode_special_point"
                  defaultValue=""
                />
              </div>
            </div>
          </>
        )}
        {watchType !== "3" && !isSeatBooking && (
          <div className="input_row">
            <div className="f_left_col">Number of participate</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.Dropdown
                    {...field}
                    options={[
                      {
                        value: "1",
                        label: "ไม่จำกัดสิทธิ์",
                      },
                      {
                        value: "2",
                        label: "จำกัดสิทธิ์",
                      },
                    ]}
                    errors={errors.permission_type?.message}
                    disabled={id !== "create" || watchType === "4"}
                  />
                )}
                name="permission_type"
                // defaultValue={{ label: '1', value: '1' }}
              />
            </div>
          </div>
        )}
        {watchPermissionType && watchPermissionType === "2" && (
          <>
            <div className="input_row">
              <div className="f_left_col">Announcement type</div>
              <div className="f_right_col">
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.Dropdown
                      {...field}
                      options={[
                        {
                          label: "Run by Que",
                          value: "1",
                        },
                        {
                          label: "Select by Admin",
                          value: "2",
                        },
                      ]}
                      errors={errors.announcement_type?.message}
                      disabled={
                        initialValues &&
                        (initialValues.status === "2" ||
                          initialValues.status === "3")
                      }
                    />
                  )}
                  name="announcement_type"
                  // defaultValue={{ label: '1', value: '1' }}
                />
              </div>
            </div>
            <div className="input_row">
              <div className="f_left_col">Fix</div>
              <div className="f_right_col">
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.TextField
                      {...field}
                      type="number"
                      min={
                        initialValues ? initialValues.permission_main_active : 1
                      }
                      // max={50}
                      // placeholder={t('test')}
                      errors={errors.permission_main_quotar?.message}
                    />
                  )}
                  name="permission_main_quotar"
                  defaultValue=""
                />
              </div>
            </div>
            {watchAnnouncementType === "2" && (
              <>
                <div className="input_row">
                  <div className="f_left_col">Sub</div>
                  <div className="f_right_col">
                    <Controller
                      control={control}
                      render={({ field }) => (
                        <Inputs.TextField
                          {...field}
                          type="number"
                          min={
                            initialValues
                              ? initialValues.permission_reserve_active
                              : 1
                          }
                          // max={10}
                          // placeholder={t('test')}
                          errors={errors.permission_reserve_quotar?.message}
                        />
                      )}
                      name="permission_reserve_quotar"
                      defaultValue=""
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
        {watchType === "2" && watchPermissionType === "2" && (
          <div className="input_row">
            <div className="f_left_col">Allow to add up follower</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextField
                    {...field}
                    type="number"
                    min={0}
                    max={2}
                    // placeholder={t('test')}
                    errors={errors.participation_limit?.message}
                  />
                )}
                name="participation_limit"
                defaultValue=""
              />
            </div>
          </div>
        )}
        <div className="input_row">
          <div className="f_left_col">Start date register</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.DateTime
                  {...field}
                  placeholder={"Registration Start date"}
                  errors={errors.registration_start_date?.message}
                  showYearDropdown
                />
              )}
              name="registration_start_date"
              // defaultValue={new Date()}
            />
          </div>
        </div>
        <div className="input_row">
          <div className="f_left_col">Start Time</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.DateTime
                  {...field}
                  showTimeSelect
                  showTimeSelectOnly
                  dateFormat="HH:mm"
                  placeholder={"Start time"}
                  errors={errors.registration_start_time?.message}
                  showYearDropdown
                />
              )}
              name="registration_start_time"
              // defaultValue={new Date()}
            />
          </div>
        </div>
        <div className="input_row">
          <div className="f_left_col">End date register</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.DateTime
                  {...field}
                  placeholder={"Registration End date"}
                  errors={errors.registration_end_date?.message}
                  showYearDropdown
                />
              )}
              name="registration_end_date"
              // defaultValue={new Date()}
            />
          </div>
        </div>
        <div className="input_row">
          <div className="f_left_col">End Time</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.DateTime
                  {...field}
                  showTimeSelect
                  showTimeSelectOnly
                  dateFormat="HH:mm"
                  placeholder={"End time"}
                  errors={errors.registration_end_time?.message}
                  showYearDropdown
                />
              )}
              name="registration_end_time"
              // defaultValue={new Date()}
            />
          </div>
        </div>
        {/* <div className="input_row">
          <div className="f_left_col">Last date register</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.DateTime
                  {...field}
                  placeholder={"Last date"}
                  errors={errors.last_date?.message}
                  showYearDropdown
                />
              )}
              name="last_date"
              // defaultValue={new Date()}
            />
          </div>
        </div> */}
        {watchAnnouncementType === "2" && (
          <div className="input_row">
            <div className="f_left_col">Announcement date</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.DateTime
                    {...field}
                    placeholder={"Announcement date"}
                    errors={errors.announcement_date?.message}
                    showYearDropdown
                  />
                )}
                name="announcement_date"
                // defaultValue={new Date()}
              />
            </div>
          </div>
        )}

        {!watchType ||
          (watchType !== "4" && (
            <>
              <div className="input_row">
                <div className="f_left_col">One time popup</div>
                <div className="f_right_col">
                  <Controller
                    control={control}
                    render={({ field }) => (
                      <Inputs.InputSwitch
                        {...field}
                        disabled={true}
                        errors={errors.is_popup_activate?.message}
                      />
                    )}
                    name="is_popup_activate"
                    // defaultValue={new Date()}
                  />
                </div>
              </div>
              <div className="input_row">
                <div className="f_left_col">One time Notification</div>
                <div className="f_right_col">
                  <Controller
                    control={control}
                    render={({ field }) => (
                      <Inputs.InputSwitch
                        {...field}
                        disabled={true}
                        errors={errors.is_notification_activate?.message}
                      />
                    )}
                    name="is_notification_activate"
                    defaultValue={false}
                  />
                </div>
              </div>
              <div className="input_row">
                <div className="f_left_col">Question status</div>
                <div className="f_right_col">
                  <Controller
                    control={control}
                    render={({ field }) => (
                      <Inputs.InputSwitch
                        {...field}
                        errors={errors.is_condition?.message}
                      />
                    )}
                    name="is_condition"
                    defaultValue={false}
                  />
                </div>
              </div>
            </>
          ))}

        {watchIsCondition === true && (
          <>
            {fields.map((item, index) => (
              <div key={item.id} className="question_block">
                <div className="question_row">
                  <div className="question_left_col">
                    <div className="question_input_item">
                      <Controller
                        render={({ field }) => (
                          <Inputs.InputWysiwyg
                            {...field}
                            label={`คำถามข้อที่ ${index + 1}`}
                          />
                        )}
                        name={`question_list.${index}.question`}
                        control={control}
                      />
                    </div>
                    <div className="question_upload">
                      <Controller
                        control={control}
                        render={({ field }) => (
                          <Inputs.UploadImage
                            {...field}
                            theme_upload_image
                            fieldName="question"
                            acceptType={"cover"}
                            label={"สามารถใส่รูปภาพประกอบได้"}
                            // scaleLabel="*** รูปขนาดกว้าง 720px สูง 720px"
                            // errors={errors.img_file?.message}
                          />
                        )}
                        name={`question_list.${index}.image`}
                        defaultValue={[]}
                      />
                    </div>
                  </div>
                  <div
                    className="btn_delete"
                    onClick={() => {
                      onDeleteQuestion && onDeleteQuestion(item);
                      remove(index);
                    }}
                  >
                    <MdDelete fontSize={25} color={"red"} />
                  </div>
                </div>
                <div className="question_type_wrap">
                  <Controller
                    render={({ field }) => (
                      <Inputs.Dropdown
                        {...field}
                        options={[
                          {
                            label: "Choice",
                            value: "1",
                          },
                          {
                            label: "Freetext",
                            value: "2",
                          },
                          {
                            label: "Upload",
                            value: "3",
                          },
                        ]}
                        errors={errors.type?.message}
                      />
                    )}
                    name={`question_list.${index}.type`}
                    control={control}
                  />
                </div>
                {watchQuestionList &&
                  watchQuestionList[index] &&
                  watchQuestionList[index].type === "1" && (
                    <div className="answer_block">
                      <AnswerForm nestIndex={index} {...{ control, errors }} />
                    </div>
                  )}
              </div>
            ))}
            <button type="button" className="btn_add" onClick={() => append()}>
              เพิ่มคำถาม
            </button>
          </>
        )}
        <div className="input_row">
          <div className="f_left_col">Active</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.InputSwitch
                  {...field}
                  errors={errors.is_active?.message}
                />
              )}
              name="is_active"
              defaultValue={false}
            />
          </div>
        </div>
        {isSeatBooking && (
          <div className="input_row">
            <div className="f_left_col">ซ่อนจากหน้ารวมกิจกรรม</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => <Inputs.InputSwitch {...field} value={!!field.value} />}
                name="is_unlisted"
                defaultValue={false}
              />
              <div>***เข้าได้เฉพาะลิงก์ (ปุ่ม Copy url) และต้องล็อกอินสมาชิก</div>
            </div>
          </div>
        )}
        {/* อยู่ใต้ Active เพราะสถานะขึ้นกับ Active + ช่วงลงทะเบียน (เปิด Active อย่างเดียวยังจองไม่ได้ ต้องตั้งค่าที่หน้า Booking Seat ครบด้วย) */}
        {isSeatBooking && seatStatus && (
          <div className="input_row seat_status_row">
            <div className="f_left_col">สถานะการจองที่นั่ง</div>
            <div className="f_right_col">
              <Displays.StatusLabel
                bg={seatStatus.isOpen ? "green" : "#FFB341CC"}
                label={seatStatus.isOpen ? "ลูกค้าจองได้" : "ลูกค้ายังจองไม่ได้"}
              />
              <div className="seat_checks">
                {seatStatus.checks.map((e) => (
                  <div key={e.label} className={`seat_check_item ${e.ok ? "ok" : "ng"}`}>
                    {e.ok ? <MdCheckCircle /> : <MdCancel />}
                    {e.label}
                  </div>
                ))}
              </div>
              <div className="seat_setting_btn">
                <Buttons.BgStandard label="ไปตั้งค่า Booking Seat" onClick={onGoSeatSetting} />
              </div>
              <div>***สถานะคิดจากข้อมูลที่บันทึกแล้ว ถ้าแก้ Active หรือช่วงลงทะเบียน ต้องกด Save ก่อน</div>
            </div>
          </div>
        )}
        <div className="input_row">
          <div className="f_left_col">PIN/Priority</div>
          <div className="f_right_col">
            <Controller
              control={control}
              render={({ field }) => (
                <Inputs.InputSwitch
                  {...field}
                  errors={errors.is_active?.message}
                />
              )}
              name="is_pin"
              defaultValue={false}
            />
          </div>
        </div>
        {id !== "create" &&
          initialValues &&
          initialValues.condition_type === "4" && (
            <div className="qr_code_block">
              <div className="f_title">QR with out Register</div>
              <div className="qrcode_row">
                <Cards.QrCodeItem
                  code={`${process.env.REACT_APP_DOMAIN}?redirect=activity-qr-register&id=${initialValues._id}`}
                />
                <Cards.QrCodeItem
                  code={`https://liff.line.me/2006522792-G8l6P6BX?redirect=activity-qr-register&id=${initialValues._id}`}
                />
              </div>
            </div>
          )}
        {id !== "create" &&
          initialValues &&
          initialValues.qrcode_list?.length > 0 &&
          // กิจกรรมจองที่นั่งใช้ตั๋ว QR ต่อที่นั่ง qrcode ของกิจกรรมแบบเดิมไม่ได้ใช้ ซ่อนกันสับสน
          !isSeatBooking && (
            <div className="qr_code_block">
              <div className="f_title">QRCode normal</div>
              <div className="qrcode_row">
                {initialValues.qrcode_list.map((e, i) => (
                  <div key={i}>
                    <Cards.QrCodeItem
                      code={
                        // condition_type "5" ฝัง id กิจกรรมไว้ใน qrcode
                        // ลูกค้าจะได้กดสแกนจากเมนู SCAN ได้เลย ไม่ต้องเข้าหน้ากิจกรรมก่อน
                        // condition_type อื่นยังเป็นรหัสเปล่าเหมือนเดิม qrcode ที่ปริ้นท์ไปแล้วไม่เสีย
                        initialValues.condition_type === "5"
                          ? `${process.env.REACT_APP_DOMAIN}?redirect=activity-scan&id=${initialValues._id}&code=${e}`
                          : e
                      }
                      // โชว์รหัสจริงใต้ qrcode แทน url ยาว ๆ และใช้ตั้งชื่อไฟล์ตอนดาวน์โหลด
                      label={e}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        {id !== "create" &&
          initialValues &&
          initialValues.is_used_qrcode_special && (
            <div className="qr_code_block">
              <div className="f_title">QRCode special</div>
              <div className="qrcode_row">
                <Cards.QrCodeItem code={initialValues.qrcode_special} />
              </div>
            </div>
          )}
        {permission && (
          <div className="action_row">
            <div className="w120">
              <Buttons.BgStandard theme_gray label="Back" onClick={onCancel} />
            </div>
            {id !== "create" && permission.is_delete && (
              <div className="w120">
                <Buttons.BgStandard
                  theme_red
                  label="Delete"
                  onClick={onDelete}
                />
              </div>
            )}
            {id !== "create" && permission.is_update ? (
              <>
                {!initialValues.is_popup_activate && (
                  <div className="w120">
                    <Buttons.BgStandard
                      theme_green
                      label="Sent popup"
                      // disabled={initialValues && initialValues.is_center}
                      onClick={onSent}
                    />
                  </div>
                )}
                <div className="w120">
                  <Buttons.BgStandard
                    type="submit"
                    label="Update"
                    // disabled={initialValues && initialValues.is_center}
                  />
                </div>
              </>
            ) : (
              permission.is_create && (
                <div className="w120">
                  <Buttons.BgStandard
                    type="submit"
                    label="Save"
                    // disabled={initialValues && initialValues.is_center}
                  />
                </div>
              )
            )}
            {initialValues && (
              <div className="w120">
                <a
                  href={`${process.env.REACT_APP_DOMAIN}/activities/admin/preview/${initialValues._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Buttons.BgStandard
                    theme_purple
                    type="button"
                    label="Preview"
                  />
                </a>
              </div>
            )}
          </div>
        )}
      </form>
    </ActivityFormStyled>
  );
};

ActivityForm.propTypes = {};

export default ActivityForm;
